import React from 'react';

interface StepIndicatorProps {
  steps: Array<{
    id: number;
    label: string;
    completed?: boolean;
  }>;
  currentStep: number;
}

const CheckIcon: React.FC = () => (
  <svg
    className="w-4 h-4 text-white"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    strokeWidth={2.5}
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const StepIndicator: React.FC<StepIndicatorProps> = ({
  steps,
  currentStep,
}) => {
  const getStepStatus = (stepId: number) => {
    if (currentStep > stepId) return 'completed';
    if (currentStep === stepId) return 'current';
    return 'upcoming';
  };

  const getStepStyles = (status: string) => {
    switch (status) {
      case 'completed':
        return {
          circle: 'bg-green-500 text-white border-green-500',
          text: 'text-green-600 font-medium',
        };
      case 'current':
        return {
          circle: 'bg-[#5E2AAC] text-white border-[#5E2AAC]',
          text: 'text-[#5E2AAC] font-medium',
        };
      case 'upcoming':
      default:
        return {
          circle: 'bg-gray-100 text-gray-400 border-gray-300',
          text: 'text-gray-400',
        };
    }
  };

  return (
    <div className="flex items-center">
      {steps.map((step, index) => {
        const status = getStepStatus(step.id);
        const styles = getStepStyles(status);
        const isCompleted = status === 'completed';

        return (
          <React.Fragment key={step.id}>
            <div className="flex items-center">
              {/* Step Circle */}
              <div
                className={`
                  w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-semibold
                  transition-all duration-200 ease-in-out
                  ${styles.circle}
                `}
              >
                {isCompleted ? <CheckIcon /> : step.id}
              </div>

              {/* Step Label */}
              <span className={`ml-2 text-sm transition-colors ${styles.text}`}>
                {step.label}
              </span>
            </div>

            {/* Arrow Separator */}
            {index < steps.length - 1 && (
              <div className="mx-3 text-gray-400">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default StepIndicator;
